import type { ChatApiVersion, ChatErrorContext, ChatService } from '@odo/shared-ui';
import { createChatServiceV2 } from '@odo/shared-ui';
import apiV2 from '../api/v2';
import {
    deleteChatMessage,
    editChatMessage,
    fetchChatStatus,
    fetchPatientChat,
    fetchPatientChatActivities,
    removeChatAttachment,
    sendChatMessage,
    uploadChatFile
} from './chat';
import { reportError } from './errorReporting';

/** files-viewer's binding of the shared chat provider to its own API layer. */
const chatService: ChatService = {
    fetchChat: fetchPatientChat,
    fetchChatStatus,
    fetchActivities: fetchPatientChatActivities,
    sendMessage: sendChatMessage,
    editMessage: editChatMessage,
    deleteMessage: deleteChatMessage,
    deleteMessageFile: removeChatAttachment,
    uploadFile: uploadChatFile,
    onError: (err: unknown, context: ChatErrorContext): void => {
        // eslint-disable-next-line no-console
        console.error(`Chat ${context.scope} failed:`, err);
        reportError({
            source: `PatientChatProvider.${context.scope}`,
            message: err instanceof Error ? err.message : String(err),
            stack: err instanceof Error ? err.stack : undefined,
            url: window.location.href,
            pathname: window.location.pathname,
            userAgent: navigator.userAgent,
            errorType: 'unhandledrejection',
            metadata: { patientId: context.patientId }
        });
    }
};

/**
 * The same port, served by /api/v2: the conversation comes from the database
 * instead of Monday. Only the upload differs from the shared factory — the
 * three-step signed-URL flow is files-viewer's own and has nothing to do with
 * which chat API is in use.
 */
const chatServiceV2: ChatService = createChatServiceV2({
    http: apiV2,
    uploadFile: uploadChatFile,
    onError: chatService.onError
});

/** Picks the implementation for a version. The only place that mapping lives. */
export function getChatService(version: ChatApiVersion): ChatService {
    return version === 'v2' ? chatServiceV2 : chatService;
}
